import React, { useEffect, useState } from "react";
import tw from "twin.macro";
import styled from "styled-components";
import {css} from "styled-components/macro"; //eslint-disable-line
import { connect } from "react-redux";
import { fetchBlogPostsData } from "services/backendCalls";

import { SectionHeading as HeadingTitle, Subheading } from "components/misc/Headings.js";
import { PrimaryButton as PrimaryButtonBase } from "components/misc/Buttons.js";
import { ReactComponent as UserIcon } from "feather-icons/dist/icons/user.svg";
import { ReactComponent as TagIcon } from "feather-icons/dist/icons/tag.svg";
import { ReactComponent as SvgDecoratorBlob1 } from "../../images/svg-decorator-blob-1.svg";
import { ReactComponent as SvgDecoratorBlob2 } from "../../images/svg-decorator-blob-3.svg";

const Container = tw.div`relative`;
const Content = tw.div`max-w-screen-xl mx-auto py-20 lg:py-24`;
const ThreeColumn = tw.div`flex flex-col items-center lg:items-stretch lg:flex-row flex-wrap`;
const Column = tw.div`mt-24 lg:w-1/3`;

const HeadingInfoContainer = tw.div`flex flex-col items-center`;
const HeadingDescription = tw.p`mt-4 font-medium text-gray-600 text-center max-w-sm`;

const Card = tw.div`lg:mx-4 xl:mx-8 max-w-sm flex flex-col h-full`;
const Image = styled.div(props => [
  `background-image: url("${props.imageSrc}");`,
  tw`bg-cover bg-center h-80 lg:h-64 rounded rounded-b-none`
]);

const Details = tw.div`p-6 rounded border-2 border-t-0 rounded-t-none border-dashed border-primary-100 flex-1 flex flex-col items-center text-center lg:block lg:text-left`;
const MetaContainer = tw.div`flex items-center`;
const Meta = styled.div`
  ${tw`text-secondary-100 font-medium text-sm flex items-center leading-none mr-6 last:mr-0`}
  svg {
    ${tw`w-4 h-4 mr-1`}
  }
`;

const Title = tw.h5`mt-4 leading-snug font-bold text-lg`;
const Description = tw.p`mt-2 text-sm text-secondary-100`;
const Link = styled(PrimaryButtonBase).attrs({as: "a"})`
  ${tw`inline-block mt-4 text-sm font-semibold`}
`;

const DecoratorBlob1 = tw(
  SvgDecoratorBlob1
)`-z-10 absolute bottom-0 right-0 w-48 h-48 transform translate-x-40 -translate-y-8 opacity-25`;
const DecoratorBlob2 = tw(
  SvgDecoratorBlob2
)`-z-10 absolute top-0 left-0 w-48 h-48 transform -translate-x-32 translate-y-full opacity-25`;

const EmptyState = tw.div`text-center py-16 w-full lg:w-1/3 lg:mx-4 xl:mx-8`;
const EmptyIcon = tw.div`text-6xl mb-4`;
const EmptyTitle = tw.h3`text-xl font-bold text-gray-700 mb-2`;
const EmptyText = tw.p`text-gray-500 text-sm`;

const ThreeColComp2 = ({
  subheading = "Blog",
  heading = <>We Love <span tw="text-primary-500">Writing.</span></>,
  description = "Some amazing blog posts that are written by even more amazing people.",

}) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPosts = async () => {
      setLoading(true);
      try {
        const fetchedPosts = await fetchBlogPostsData();
        setPosts(Array.isArray(fetchedPosts) ? fetchedPosts : []);
      } catch (e) {
        console.error('Failed to load blog posts:', e);
        setPosts([]);
      } finally {
        setLoading(false);
      }
    };

    loadPosts();
  }, []);

  if (loading) {
    return (
      <Container>
        <Content>
          <HeadingInfoContainer>
            {subheading && <Subheading>{subheading}</Subheading>}
            <HeadingTitle>{heading}</HeadingTitle>
            <HeadingDescription>Loading blog posts...</HeadingDescription>
          </HeadingInfoContainer>
          <ThreeColumn>
            <div className="text-center py-12 lg:w-1/3 lg:mx-4 xl:mx-8">
              <div className="animate-spin inline-block w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full mx-auto" />
              <p className="mt-2 text-gray-500">Loading...</p>
            </div>
          </ThreeColumn>
          <DecoratorBlob1 />
          <DecoratorBlob2 />
        </Content>
      </Container>
    );
  }

  if (posts.length === 0) {
    return (
      <Container>
        <Content>
          <HeadingInfoContainer>
            {subheading && <Subheading>{subheading}</Subheading>}
            <HeadingTitle>{heading}</HeadingTitle>
            <HeadingDescription>No blog posts available at the moment.</HeadingDescription>
          </HeadingInfoContainer>
          <ThreeColumn>
            <EmptyState>
              <EmptyIcon><span role="img" aria-label="blog posts">📝</span></EmptyIcon>
              <EmptyTitle>No Blog Posts Yet</EmptyTitle>
              <EmptyText>Blog posts will appear here once published.</EmptyText>
            </EmptyState>
          </ThreeColumn>
          <DecoratorBlob1 />
          <DecoratorBlob2 />
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <Content>
        <HeadingInfoContainer>
          {subheading && <Subheading>{subheading}</Subheading>}
          <HeadingTitle>{heading}</HeadingTitle>
          <HeadingDescription>{description}</HeadingDescription>
        </HeadingInfoContainer>
        <ThreeColumn>
          {posts.slice(0, 3).map((post, index) => (
            <Column key={index}>
              <Card>
                <Image imageSrc={post.imageSrc || post.image || ""} />
                <Details>
                  <MetaContainer>
                    <Meta>
                      <UserIcon />
                      <div>{post.author || post.locationText || "Admin"}</div>
                    </Meta>
                    <Meta>
                      <TagIcon />
                      <div>{post.category || post.pricingText || "General"}</div>
                    </Meta>
                  </MetaContainer>
                  <Title>{post.title || post.heading || "Untitled Post"}</Title>
                  <Description>{post.description || post.excerpt || ""}</Description>
                  <Link href={post.link || post.url || "#"}>Read Post</Link>
                </Details>
              </Card>
            </Column>
          ))}
        </ThreeColumn>
      </Content>
      <DecoratorBlob1 />
      <DecoratorBlob2 />
    </Container>
  );
};

const ThreeColSimpleWithImageAndDashedBorder = connect((state) => ({
  BlogData: state.BlogData
}))(ThreeColComp2);

export default ThreeColSimpleWithImageAndDashedBorder;